/* eslint-disable @typescript-eslint/require-array-sort-compare */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
import {
    Box,
    Stack,
    Typography
} from '@mui/material';
import {
    DataGrid
} from '@mui/x-data-grid';
import * as React from 'react';
import ExportExcel from '../common/ExportExcel';
import { useGetStatsQuery } from "../redux/stats";
import { EquipmentUpgradePriorityData, EquipmentUpgradePriorityReport } from './types/stats';
import PlantsFilter, { FilterData } from './PlantsFilter';
import { getFormatedStringFromDate } from '../utils/dateUtils';
import { getEquipmentUpgradeColumns } from './equipmentUpgrade.DatagridColumns';
import { useNavigate } from 'react-router-dom';
import { mergeDataGridConfig } from '../styles/DataGridConfig';

/**
 * Equipment Upgrade Priority Component
 * 
 * Shows field services colleagues the priority of their actions by customers 
 * concerning cutters software upgrades and interventions.
 */

const EquipmentUpgradePriority: React.FC = (): React.ReactElement => {
    const results: ReturnType<typeof useGetStatsQuery> = useGetStatsQuery('Equipment_Upgrade_Priority', { skip: false });
    const navigate = useNavigate();

    const [filters, setFilters] = React.useState<FilterData>({
        region: '',
        country: '',
        company: ''
    });
    const [filteredData, setFilteredData] = React.useState<EquipmentUpgradePriorityData[]>([]);

    // Extract data from results
    const rawData: EquipmentUpgradePriorityData[] = React.useMemo((): EquipmentUpgradePriorityData[] => {
        if (results.isLoading || results.isError || !results.data) {
            return [];
        }

        const report = results.data as EquipmentUpgradePriorityReport;
        return report.reportData || [];
    }, [results.data, results.isLoading, results.isError]);

    // Calculate summary statistics
    const summaryStats = React.useMemo(() => {
        if (!filteredData.length) {
            return {
                totalCutters: 0,
                highPriority: 0,
                mediumPriority: 0,
                lowPriority: 0,
                averageUpgradeIndex: 0,
                contractedCutters: 0,
                contractRate: 0
            };
        }

        const highPriority = filteredData.filter(item => item.RegionalRank <= 10).length;
        const mediumPriority = filteredData.filter(item => item.RegionalRank > 10 && item.RegionalRank <= 25).length;
        const lowPriority = filteredData.filter(item => item.RegionalRank > 25).length;
        const totalUpgradeIndex = filteredData.reduce((sum, item) => sum + (item.UpgradeNeededIndex || 0), 0);
        const contractedCutters = filteredData.filter(item => item.HasContract === 1).length;

        return {
            totalCutters: filteredData.length,
            highPriority,
            mediumPriority,
            lowPriority,
            averageUpgradeIndex: totalUpgradeIndex / filteredData.length,
            contractedCutters,
            contractRate: (contractedCutters / filteredData.length) * 100
        };
    }, [filteredData]);

    // Show loading state
    if (results.isLoading) {
        return (
            <Stack direction="column" spacing={3} sx={{ p: 3 }}>
                <Typography variant="h6">Loading Equipment Upgrade Priority data...</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <Typography>Please wait while we fetch the latest upgrade priority information.</Typography>
                </Box>
            </Stack>
        );
    }

    // Show error state
    if (results.isError) {
        return (
            <Stack direction="column" spacing={3} sx={{ p: 3 }}>
                <Typography variant="h6" color="error">Error loading Equipment Upgrade Priority data</Typography>
                <Typography>There was an error fetching the upgrade priority data. Please try again later.</Typography>
            </Stack>
        );
    }

    return (
        <Stack direction="column" sx={{ display: 'flex', mb: 2, gap: 1, 
            marginX: 0, paddingX:0, 
            marginTop: '1em' }}>
            {/* Header Section */}
            <Stack direction="row"  sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1, marginX: '1em'
                }}>
                <Typography variant="body2" fontSize="75%" sx={{ mb: 2 }}>
                    This dashboard displays equipment upgrade needs according gathered data, <b>meaning maybe not applicable on field</b>:
                    <ul>
                        <li><b>Upgrade Index</b>: Percentage indicating prominence of software/hardware update need</li>
                        <li><b>Service Metrics</b>: Connection rates, software update indices, and contract coverage</li>
                        <li><b>Equipment Details</b>: Machine types, models, delivery dates, and customer information</li>
                    </ul>
                    <p><b>Use filters and search</b> to find specific cutters or regions. Sort by priority for action planning.</p>
                </Typography>
                <Typography variant="body2" fontSize="75%" sx={{ mb: 2 }}>
                    Last updated: <b>{getFormatedStringFromDate((results.data as EquipmentUpgradePriorityReport).createdAt)}</b><br />
                    <br />
                    <b>Data and analysis are corrected over time.</b>
                </Typography>
                {(filteredData?.length ?? 0) > 0 && (
                <ExportExcel label={`Equipment_Upgrade_Needs`}
                    total={filteredData.length} results={filteredData}
                    svg={{ width: '2rem', fill: '#0E796E' }} sx={{ mb: 2 }}
                    title={`Export ${filteredData.length} 'Equipment_Deliveries' results`} />
                )}
            </Stack>
            {/* Filter Section */}
            <PlantsFilter sx={{ borderTop: '1px solid #ccc', borderBottom: '0px solid #ccc', 
            marginX: '2em', paddingBottom: 0, marginBottom: 0 }}
                    data={rawData}
                    filters={filters}
                    onFiltersChange={setFilters}
                    onFilteredDataChange={setFilteredData}
                    dataCountLabel="cutters"
                    showDataCount={true}
            />
            {/* DataGrid Section */}
            <DataGrid
                rows={filteredData}
                columns={getEquipmentUpgradeColumns(navigate)}
                getRowId={(row) => row.SerialNumber}
                pagination
                paginationMode="client"
                pageSizeOptions={[10, 25, 50, 100, 200]}
                initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                    sorting: {
                        sortModel: [{ field: 'RegionalRank', sort: 'asc' }],
                    },
                }}
                {...mergeDataGridConfig()}
            />
            {/* Summary Statistics */}
            <Stack direction="row" spacing={3} sx={{ mt: 2, justifyContent: 'center' }}>
                    <Box>
                        <Typography variant="h6" color="primary">
                            {summaryStats.totalCutters.toLocaleString()}
                        </Typography>
                        <Typography variant="caption">Total Cutters</Typography>
                    </Box>
                    <Box>
                        <Typography variant="h6" color="error">
                            {summaryStats.highPriority}
                        </Typography>
                        <Typography variant="caption">High Priority (1-10)</Typography>
                    </Box>
                    <Box>
                        <Typography variant="h6" color="warning.main">
                            {summaryStats.mediumPriority}
                        </Typography>
                        <Typography variant="caption">Medium Priority (11-25)</Typography>
                    </Box>
                    <Box>
                        <Typography variant="h6" color="success.main">
                            {summaryStats.lowPriority}
                        </Typography>
                        <Typography variant="caption">Lower Priority (26+)</Typography>
                    </Box>
                    <Box>
                        <Typography variant="h6" color="secondary">
                            {summaryStats.averageUpgradeIndex.toFixed(1)}%
                        </Typography>
                        <Typography variant="caption">Avg. Upgrade Index</Typography>
                    </Box>
                    <Box>
                        <Typography variant="h6" color="info.main">
                            {summaryStats.contractRate.toFixed(1)}%
                        </Typography>
                        <Typography variant="caption">Contract Coverage</Typography>
                    </Box>
            </Stack>
        </Stack>
    );
};

export default EquipmentUpgradePriority;